'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var min = _interopDefault(require('ml-array-min'));
var max = _interopDefault(require('ml-array-max'));
var mlGsd = require('ml-gsd');
var equallySpaced = _interopDefault(require('ml-array-xy-equally-spaced'));
var Util = _interopDefault(require('ml-array-utils'));
var jcampconverter = require('jcampconverter');
var xyParser = require('xy-parser');

const ABSORBANCE = 1;
const TRANSMITTANCE = 2;
const PERCENT_TRANSMITTANCE = 3;

function getKind(kind = '') {
  if (typeof kind === 'number') {
    if (kind < 1 || kind > 2) {
      throw new Error('kind should either be 1 or 2');
    }
    return kind;
  }
  if (kind.match(/abs/i)) {
    return ABSORBANCE;
  }
  return TRANSMITTANCE;
}

/**
 * Create an object of Chromatogram
 * @return {object}
 */
function toJSON() {
  return {
    wavelength: this.wavelength,
    transmittance: this.transmittance,
    peaks: this.peaks
  };
}

function getAnnotations(spectrum, options = {}) {
  const { fillColor = 'green', strokeColor = 'red', creationFct } = options;
  const peaks = spectrum.peaks;
  if (!peaks) return [];
  let annotations = peaks.map((peak) => {
    var annotation = {
      line: 1,
      type: 'rect',
      strokeColor: strokeColor,
      strokeWidth: 0,
      fillColor: fillColor
    };
    if (creationFct) {
      creationFct(annotation, peak);
    }
    switch (spectrum.mode) {
      case ABSORBANCE:
        annotationAbsorbance(annotation, peak);
        break;
      case TRANSMITTANCE:
        annotationTransmittance(annotation, peak, 1);
        break;
      case PERCENT_TRANSMITTANCE:
        annotationTransmittance(annotation, peak, 100);
        break;
      default:
    }
    return annotation;
  });

  return annotations;
}

function annotationTransmittance(annotation, peak, factor = 1) {
  annotation.label = [
    {
      text: peak.kind,
      size: '18px',
      anchor: 'middle',
      color: 'red',
      position: {
        x: peak.wavelength,
        y: peak.transmittance * factor,
        dy: '23px'
      }
    }
  ];
  annotation.position = [
    {
      x: peak.wavelength,
      y: peak.transmittance * factor,
      dy: '10px',
      dx: '-1px'
    },
    {
      x: peak.wavelength,
      y: peak.transmittance * factor,
      dy: '5px',
      dx: '1px'
    }
  ];
}

function annotationAbsorbance(annotation, peak) {
  annotation.label = [
    {
      text: peak.kind,
      size: '18px',
      anchor: 'middle',
      color: 'red',
      position: {
        x: peak.wavelength,
        y: peak.absorbance,
        dy: '-15px'
      }
    }
  ];
  annotation.position = [
    {
      x: peak.wavelength,
      y: peak.absorbance,
      dy: '-10px',
      dx: '-1px'
    },
    {
      x: peak.wavelength,
      y: peak.absorbance,
      dy: '-5px',
      dx: '1px'
    }
  ];
}

/**
 *
 * @param {*} spectrum
 * @param {object} [options={}]
 *
 */

function getData(spectrum, options = []) {
  switch (spectrum.mode) {
    case ABSORBANCE:
      return spectrum.getAbsorbance();
    case TRANSMITTANCE:
      return spectrum.getTransmittance();
    case PERCENT_TRANSMITTANCE:
      return spectrum.getPercentTransmittance();
    default:
      return {};
  }
}

/**
 *
 * @param {Spectrum} spectrum
 * @param {object} peak
 */

function addPeak(spectrum, peak = {}) {
  if (!peak.wavelength) {
    throw new Error('addPeak: peak mush have wavelength property');
  }
  if (!peak.absorbance && !peak.transmittance) {
    throw new Error(
      'addPeak: peak mush have either absorbance of transmittance property'
    );
  }
  const {
    wavelength,
    transmittance = 10 ** -peak.absorbance,
    absorbance = -Math.log10(peak.transmittance)
  } = peak;

  for (let existing of spectrum.peaks) {
    if (Number(existing.wavelength) === wavelength) return existing;
  }
  spectrum.peaks.push({
    wavelength: wavelength,
    transmittance: transmittance,
    absorbance: absorbance,
    kind: getPeakKind(
      transmittance,
      spectrum.minTransmittance,
      spectrum.maxTransmittance
    )
  });
  return peak;
}

function getPeakKind(transmittance, minTransmittance, maxTransmittance) {
  var position =
    (maxTransmittance - transmittance) / (maxTransmittance - minTransmittance);
  if (position < 0.33) {
    return 'w';
  } else if (position < 0.66) {
    return 'm';
  }
  return 'S';
}

/**
 *
 * @param {Spectrum} spectrum
 * @param {number} targetWavelength
 * @param {object} [options]
 * @param {number} [options.range=0] Search in a range around the targetWavelength
 * @param {boolean} [options.optimize=false] Search for the closest peak to the targetWavelength
 */

function peakPicking(spectrum, targetWavelength, options = {}) {
  const { range = 0, optimize = false } = options;

  // find the peak that is the closest to the click
  let bestPeak = getClosest(spectrum, targetWavelength);

  if (optimize) {
    findClosest(spectrum, bestPeak);
  } else if (range) {
    bestInRange(spectrum, bestPeak, targetWavelength, range);
  }

  return addPeak(spectrum, bestPeak);
}

function getClosest(spectrum, targetWavelength) {
  let bestPeak = {
    transmittance: spectrum.transmittance[0],
    absorbance: spectrum.absorbance[0],
    wavelength: spectrum.wavelength[0],
    index: 0
  };

  let error = Math.abs(targetWavelength - bestPeak.wavelength);
  for (let i = 1; i < spectrum.wavelength.length; i++) {
    let newError = Math.abs(targetWavelength - spectrum.wavelength[i]);
    if (newError < error) {
      error = newError;
      setBestPeak(spectrum, bestPeak, i);
    }
  }
  return bestPeak;
}

function bestInRange(spectrum, bestPeak, targetWavelength, range) {
  // we search the minimum based on wavelength +/- range
  for (let i = 0; i < spectrum.wavelength.length; i++) {
    if (Math.abs(spectrum.wavelength[i] - targetWavelength) <= range) {
      if (spectrum.transmittance[i] < bestPeak.transmittance) {
        setBestPeak(spectrum, bestPeak, i);
      }
    }
  }
}

function findClosest(spectrum, bestPeak) {
  let index = bestPeak.index;
  let previousIndex;
  while (index !== previousIndex) {
    previousIndex = index;
    if (index > 0 && spectrum.absorbance[index - 1] > bestPeak.absorbance) {
      index--;
      setBestPeak(spectrum, bestPeak, index);
    } else if (
      index < spectrum.wavelength.length - 1 &&
      spectrum.absorbance[index + 1] > bestPeak.absorbance
    ) {
      index++;
      setBestPeak(spectrum, bestPeak, index);
    }
  }
}

function setBestPeak(spectrum, bestPeak, index) {
  bestPeak.index = index;
  bestPeak.wavelength = spectrum.wavelength[index];
  bestPeak.absorbance = spectrum.absorbance[index];
  bestPeak.transmittance = spectrum.transmittance[index];
}

/**
 *
 * @param {*} spectrum
 * @param {object} [options={}]
 * @param {number} [options.fromWavelength=0]
 * @param {number} [options.toWavelength=5000]
 * @param {number} [options.noiseLevel=0.01] - Specify the level of the noise
 * @param {number} [options.minMaxRatio=0.05] - Threshold to determine if a given peak should be considered as a noise
 * @param {boolean} [options.replaceExisting=true] - Replace existing peaks
 */

function autoPeakPicking(spectrum, options = {}) {
  const {
    noiseLevel = 0.001,
    minMaxRatio = 0.05,
    fromWavelength = 0,
    toWavelength = 5000,
    replaceExisting = true
  } = options;

  let peaks = mlGsd.gsd(spectrum.wavelength, spectrum.absorbance, {
    noiseLevel,
    minMaxRatio,
    realTopDetection: true,
    maxCriteria: true,
    smoothY: false,
    sgOptions: { windowSize: 7, polynomial: 3 }
  });

  peaks = peaks.filter(
    (peak) => peak.x >= fromWavelength && peak.x <= toWavelength
  );

  if (replaceExisting) {
    while (spectrum.peaks.length) {
      spectrum.peaks.pop();
    }
  }

  peakLoop: for (let peak of peaks) {
    for (let existing of spectrum.peaks) {
      if (Number(existing.wavelength) === Number(peak.x)) continue peakLoop;
    }
    addPeak(spectrum, {
      wavelength: peak.x,
      absorbance: peak.y
    });
  }
}

function getNormalized(spectrum, options = {}) {
  const {
    from = 800,
    to = 4000,
    numberOfPoints = 1000,
    applySNV = true,
    exclusions = []
  } = options;
  let y = applySNV ? Util.SNV(spectrum.absorbance) : spectrum.absorbance;
  return equallySpaced(
    { x: spectrum.wavelength, y },
    { from, to, numberOfPoints, exclusions }
  );
}

/**
 * Class allowing manipulate one IR spectrum
 * @class spectrum
 * @param {object} [json={}] - object containing a spectrum
 * @param {Array} [json.wavelength=[]] - wavelength
 * @param {Array} [json.y=[]] - y values
 * @param {integer} [json.kind=spectrum.TRANSMITTANCE] - either spectrum.ABSORBANCE or spectrum.TRANSMITTANCE
 */
class Spectrum {
  constructor(json = {}) {
    this.wavelength = json.wavelength || [];
    this.absorbance = json.absorbance || [];
    this.transmittance = json.transmittance || [];
    this.mode = PERCENT_TRANSMITTANCE;
    this.peaks = [];
    check(this);
  }

  /**
   *
   * @param {Array} [peaks=[]] array of peaks. Peaks are composed of transmittance, wavelength, kind
   */
  setPeaks(peaks = []) {
    this.peaks = peaks;
  }

  setMode(mode) {
    if (mode < 1 || mode > 3) {
      throw new Error(
        'Mode should be either 1 (absorbance), 2 (transmittance) or 3 (percent transmittance)'
      );
    }
    this.mode = mode;
  }

  peakPicking(targetWavelength, options = {}) {
    peakPicking(this, targetWavelength, options);
  }

  getAbsorbance() {
    return { x: this.wavelength, y: this.absorbance };
  }

  getTransmittance() {
    return { x: this.wavelength, y: this.transmittance };
  }

  getPercentTransmittance() {
    let data = this.getTransmittance();
    return {
      x: data.x,
      y: data.y.map((transmittance) => transmittance * 100)
    };
  }

  getYLabel() {
    switch (this.mode) {
      case ABSORBANCE:
        return 'Absorbance';
      case TRANSMITTANCE:
        return 'Transmittance';
      case PERCENT_TRANSMITTANCE:
        return 'Transmittance [%]';
      default:
        return '';
    }
  }
}

Spectrum.prototype.toJSON = toJSON;
Spectrum.prototype.getAnnotations = function (options) {
  return getAnnotations(this, options);
};
Spectrum.prototype.getData = function (options) {
  return getData(this, options);
};
Spectrum.prototype.autoPeakPicking = function (options) {
  return autoPeakPicking(this, options);
};
Spectrum.prototype.getNormalized = function (options) {
  return getNormalized(this, options);
};

function check(spectrum) {
  if (spectrum.transmittance.length > 0 && spectrum.absorbance.length === 0) {
    spectrum.absorbance = spectrum.transmittance.map(
      (transmittance) => -Math.log10(transmittance)
    );
  }

  if (spectrum.absorbance.length > 0 && spectrum.transmittance.length === 0) {
    spectrum.transmittance = spectrum.absorbance.map(
      (absorbance) => 10 ** -absorbance
    );
  }

  if (spectrum.wavelength.length > 0) {
    spectrum.minWavelength = min(spectrum.wavelength);
  }
  if (spectrum.wavelength.length > 0) {
    spectrum.maxWavelength = max(spectrum.wavelength);
  }
  if (spectrum.absorbance.length > 0) {
    spectrum.minAbsorbance = min(spectrum.absorbance);
  }
  if (spectrum.absorbance.length > 0) {
    spectrum.maxAbsorbance = max(spectrum.absorbance);
  }
  if (spectrum.transmittance.length > 0) {
    spectrum.minTransmittance = min(spectrum.transmittance);
  }
  if (spectrum.transmittance.length > 0) {
    spectrum.maxTransmittance = max(spectrum.transmittance);
  }
}

class Spectra {
  constructor(options = {}) {
    this.from = options.from === undefined ? 800 : options.from;
    this.to = options.to === undefined ? 4000 : options.to;
    this.numberOfPoints =
      options.numberOfPoints === undefined ? 1000 : options.numberOfPoints;
    this.applySNV = options.applySNV === undefined ? true : options.applySNV;
    this.data = [];
    this.mode = PERCENT_TRANSMITTANCE;
  }

  /**
   * Add a spectrum
   * @param {Spectrum} spectrum
   * @param {string} id
   * @param {object} [meta={}]
   * @param {string} [meta.color]
   */
  addSpectrum(spectrum, id, meta = {}) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) index = this.data.length;
    this.data[index] = {
      normalized: spectrum.getNormalized(spectrum, {
        from: this.from,
        to: this.to,
        numberOfPoints: this.numberOfPoints
      }),
      spectrum,
      id,
      meta
    };
  }

  /**
   * Add jcamp
   * @param {string} jcamp
   * @param {string} id
   * @param {boolean} [force=false]
   * @param {object} [meta={}]
   * @param {string} [meta.color]
   */
  addFromJcamp(jcamp, id, meta = {}, force = false) {
    if (force === false && this.contains(id)) return;
    let spectrum = fromJcamp(jcamp);
    this.addSpectrum(spectrum, id, meta);
  }

  removeSpectrum(id) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) return undefined;
    return this.data.splice(index, 1);
  }

  contains(id) {
    return !isNaN(this.getSpectrumIndex(id));
  }

  getSpectrumIndex(id) {
    if (!id) return undefined;
    for (let i = 0; i < this.data.length; i++) {
      let spectrum = this.data[i];
      if (spectrum.id === id) return i;
    }
    return undefined;
  }

  getNormalizedData() {
    if (!this.data || !this.data[0]) return {};
    let matrix = [];
    let meta = [];
    let ids = [];
    for (let datum of this.data) {
      ids.push(datum.id);
      matrix.push(datum.normalized.y);
      meta.push(datum.meta);
    }
    let x = this.data[0].normalized.x;
    return { ids, matrix, meta, x };
  }

  getChart(options = {}) {
    const { filter = {} } = options;
    let chart = {
      title: 'IR spectra superimposition',
      data: []
    };
    for (let datum of this.data) {
      if (!filter.ids || filter.ids.includes(datum.id)) {
        let data = datum.spectrum.getData();

        data.styles = {
          unselected: {
            lineColor: datum.meta.color || 'darkgrey',
            lineWidth: 1,
            lineStyle: 1
          },
          selected: {
            lineColor: datum.meta.color || 'darkgrey',
            lineWidth: 3,
            lineStyle: 1
          }
        };
        data.label = datum.meta.id || datum.id;
        chart.data.push(data);
      }
    }
    return chart;
  }
}

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} jcamp - String containing the JCAMP data
 * @return {Spectrum} - New class element with the given data
 */
function fromJcamp(jcamp) {
  const data = jcampconverter.convert(jcamp, { xy: true });
  let spectrum = data.spectra[0].data[0];
  if (getKind(data.spectra[0].yUnit) === TRANSMITTANCE) {
    return new Spectrum({
      wavelength: spectrum.x,
      transmittance: spectrum.y,
      absorbance: []
    });
  } else {
    return new Spectrum({
      wavelength: spectrum.x,
      transmittance: [],
      absorbance: spectrum.y
    });
  }
}

/**
 * Creates a new Chromatogram element based in a Txt string
 * @param {string} text - String containing the data as CSV or TSV
 * @param {object} [options] - Options object for the parser
 * @param {string} [options.kind] - Absorbance or Transmisstance
 * @return {Spectrum} - New class element with the given data
 */
function fromText(text, options = {}) {
  options = Object.assign({}, options, { arrayType: 'xxyy' });
  const data = xyParser.parseXY(text, options);
  if (getKind(options.kind) === TRANSMITTANCE) {
    return new Spectrum({
      wavelength: data[0],
      transmittance: data[1],
      absorbance: []
    });
  } else {
    return new Spectrum({
      wavelength: data[0],
      transmisttance: [],
      absorbance: data[1]
    });
  }
}

exports.ABSORBANCE = ABSORBANCE;
exports.PERCENT_TRANSMITTANCE = PERCENT_TRANSMITTANCE;
exports.Spectra = Spectra;
exports.Spectrum = Spectrum;
exports.TRANSMITTANCE = TRANSMITTANCE;
exports.fromJcamp = fromJcamp;
exports.fromText = fromText;
